import { RouterLocation } from 'connected-react-router'
import { Location } from 'history'
import queryString from 'query-string'
import { match, matchPath, RouteProps } from 'react-router'
import {
  createSelector,
  OutputSelector,
  SelectorArray,
  UnknownMemoizer,
} from 'reselect'
import {
  authedFlyoutRoutePropsWithBackgroundLocations,
  authedLeftFlyoutRoutePropsWithBackgroundLocations,
  authedPopupRoutePropsWithBackgroundLocations,
  authedRightFlyoutRoutePropsWithBackgroundLocations,
  unauthedRightFlyoutRoutePropsWithBackgroundLocations,
} from 'src/navigation/modalBackgrounds'
import {
  accountBudgetsRouteProps,
  accountHistoryRouteProps,
  accountOverviewRouteProps,
  accountRouteProps,
  accountsAccountsRouteProps,
  accountsBudgetsRouteProps,
  accountSettingsRouteProps,
  accountsRequestsRouteProps,
  accountsRouteProps,
  accountsVirtualCardsRouteProps,
  accountTransactionsRouteProps,
  accountVirtualCardsRouteProps,
  activityRouteProps,
  apiKeysProfileRouteProps,
  apiKeysRouteProps,
  automationRulesRouteProps,
  billPayAllRouteProps,
  billPayNeedsApprovalRouteProps,
  billPayRouteProps,
  budgetHistoryRouteProps,
  budgetOverviewRouteProps,
  budgetRouteProps,
  budgetsAllBudgetsRouteProps,
  budgetsNeedsApprovalRouteProps,
  budgetsRequestsRouteProps,
  budgetsRouteProps,
  budgetTransactionsRouteProps,
  budgetVirtualCardsRouteProps,
  businessRulesRouteProps,
  cardsPhysicalCardsRouteProps,
  cardsRouteProps,
  departmentDetailsRouteProps,
  departmentsRouteProps,
  expenseCategoriesRouteProps,
  expenseCategoryLabelsRouteProps,
  expenseReportRouteProps,
  expensesAllReportsRouteProps,
  expenseSettingsRouteProps,
  expensesExpenseReportsRouteProps,
  expensesNeedsApprovalRouteProps,
  expensesOutOfPocketReportsAllRouteProps,
  expensesOutOfPocketReportsRouteProps,
  expensesOutOfPocketReportsYoursRouteProps,
  expensesRouteProps,
  expensesTransactionsRouteProps,
  expensesYourReportsRouteProps,
  helpRouteProps,
  homeRouteProps,
  invitePeopleRouteProps,
  managerReviewReimbursementRouteProps,
  managerReviewReimbursementsRouteProps,
  managerReviewTransactionsRouteProps,
  myWalletBudgetsPendingRouteProps,
  myWalletBudgetsRouteProps,
  myWalletPhysicalCardsRouteProps,
  myWalletReimbursementRouteProps,
  myWalletRouteProps,
  myWalletTransactionsRouteProps,
  myWalletVirtualCardDetailRouteProps,
  //myWalletTransactionsRouteProps,
  myWalletVirtualCardsRouteProps,
  organizationInfoRouteProps,
  orgExpenseReportRouteProps,
  orgExpenseReportsHistoricalReportsRouteProps,
  orgIntegrationsRouteProps,
  peopleInvitedToOrgDetailRouteProps,
  peopleInvitedToOrgRouteProps,
  peopleMembersDetailRouteProps,
  peopleMembersRouteProps,
  peopleRouteProps,
  profileDeleteRouteProps,
  profileInfoRouteProps,
  profileNotificationsRouteProps,
  profileOverviewRouteProps,
  profileRouteProps,
  profileSecurityRouteProps,
  receiptRulesRouteProps,
  searchRouteProps,
  transactionsRouteProps,
  virtualCardDetailRouteProps,
  virtualCardsAllCardsRouteProps,
  virtualCardsNeedsApprovalRouteProps,
  virtualCardsRequestsRouteProps,
  virtualCardsRouteProps,
  virtualCardsYourCardsRouteProps,
  yourCardsActiveRouteProps,
  yourCardsInactiveRouteProps,
  yourCardsRouteProps
} from 'src/navigation/routeProps'
import {
  LocationState,
  RootState,
  SearchQueryParams,
} from 'src/redux/createRootReducer'

export const selectLocationFromState = (
  state: RootState,
): RouterLocation<LocationState> => state.router.location

export const selectQueryParams = createSelector(
  [selectLocationFromState],
  (location) => {
    return queryString.parse(location.search) as SearchQueryParams
  },
)

export const findRouteBackgroundLocation = (
  location: Location<LocationState>,
): Location<LocationState> => {
  const matchedFlyout =
    authedFlyoutRoutePropsWithBackgroundLocations.find(
      (r) =>
        matchPath(location.pathname, r.routeProps) &&
        !r.exceptionRouteProps?.some((exc) =>
          matchPath(location.pathname, exc),
        ),
    ) ||
    unauthedRightFlyoutRoutePropsWithBackgroundLocations.find(
      (r) =>
        matchPath(location.pathname, r.routeProps) &&
        !r.exceptionRouteProps?.some((exc) =>
          matchPath(location.pathname, exc),
        ),
    )
  return matchedFlyout !== undefined
    ? matchedFlyout.calculateDefaultBackgroundLocation(location)
    : location
}
export const selectCalculatedBackgroundLocation = createSelector(
  [selectLocationFromState],
  (location) => {
    return location.state && location.state.backgroundLocation
      ? location.state.backgroundLocation
      : findRouteBackgroundLocation(location)
  },
)

export const selectBackgroundQueryParams = createSelector(
  [selectCalculatedBackgroundLocation],
  (backgroundLocation) => {
    return queryString.parse(backgroundLocation.search) as SearchQueryParams
  },
)

export const selectIsPopupOpen = createSelector(
  [selectLocationFromState],
  (location) => {
    return authedPopupRoutePropsWithBackgroundLocations.some((r) =>
      Boolean(
        matchPath(location.pathname, r.routeProps) &&
          !r.exceptionRouteProps?.some((exc) =>
            matchPath(location.pathname, exc),
          ),
      ),
    )
  },
)

export const selectIsLeftFlyoutOpen = createSelector(
  [selectLocationFromState],
  (location) => {
    return authedLeftFlyoutRoutePropsWithBackgroundLocations.some((r) =>
      Boolean(
        matchPath(location.pathname, r.routeProps) &&
          !r.exceptionRouteProps?.some((exc) =>
            matchPath(location.pathname, exc),
          ),
      ),
    )
  },
)

export const selectIsRightFlyoutOpen = createSelector(
  [selectLocationFromState],
  (location) => {
    return authedRightFlyoutRoutePropsWithBackgroundLocations.some((r) =>
      Boolean(
        matchPath(location.pathname, r.routeProps) &&
          !r.exceptionRouteProps?.some((exc) =>
            matchPath(location.pathname, exc),
          ),
      ),
    )
  },
)

// background location matches
const generateSelectBackgroundLocationMatch = <T>(
  routeProps: RouteProps,
): OutputSelector<
  SelectorArray,
  match<{ [K in keyof T]?: string }> | null,
  UnknownMemoizer
> =>
  createSelector([selectCalculatedBackgroundLocation], (backgroundLocation) =>
    matchPath(backgroundLocation.pathname, routeProps),
  )

export const selectHomeMatch =
  generateSelectBackgroundLocationMatch(homeRouteProps)
export const selectYourCardsMatch =
  generateSelectBackgroundLocationMatch(yourCardsRouteProps)
export const selectYourCardsActiveMatch = generateSelectBackgroundLocationMatch(
  yourCardsActiveRouteProps,
)
export const selectYourCardsInactiveMatch =
  generateSelectBackgroundLocationMatch(yourCardsInactiveRouteProps)
export const selectActivityMatch =
  generateSelectBackgroundLocationMatch(activityRouteProps)
export const selectAccountsMatch =
  generateSelectBackgroundLocationMatch(accountsRouteProps)
export const selectAccountsAccountsMatch =
  generateSelectBackgroundLocationMatch(accountsAccountsRouteProps)
export const selectAccountsBudgetsMatch = generateSelectBackgroundLocationMatch(
  accountsBudgetsRouteProps,
)
export const selectAccountsVirtualCardsMatch =
  generateSelectBackgroundLocationMatch(accountsVirtualCardsRouteProps)
export const selectAccountsRequestsMatch =
  generateSelectBackgroundLocationMatch(accountsRequestsRouteProps)
export const selectAccountMatch =
  generateSelectBackgroundLocationMatch(accountRouteProps)
export const selectAccountOverviewMatch = generateSelectBackgroundLocationMatch(
  accountOverviewRouteProps,
)
export const selectAccountBudgetsMatch = generateSelectBackgroundLocationMatch(
  accountBudgetsRouteProps,
)
export const selectAccountVirtualCardsMatch =
  generateSelectBackgroundLocationMatch(accountVirtualCardsRouteProps)
export const selectAccountTransactionsMatch =
  generateSelectBackgroundLocationMatch(accountTransactionsRouteProps)
export const selectAccountHistoryMatch = generateSelectBackgroundLocationMatch(
  accountHistoryRouteProps,
)
export const selectAccountSettingsMatch = generateSelectBackgroundLocationMatch(
  accountSettingsRouteProps,
)
export const selectBudgetsMatch =
  generateSelectBackgroundLocationMatch(budgetsRouteProps)
export const selectBudgetMatch =
  generateSelectBackgroundLocationMatch(budgetRouteProps)
export const selectBudgetOverviewMatch = generateSelectBackgroundLocationMatch(
  budgetOverviewRouteProps,
)
export const selectBudgetVirtualCardsMatch =
  generateSelectBackgroundLocationMatch(budgetVirtualCardsRouteProps)
export const selectBudgetTransactionsMatch =
  generateSelectBackgroundLocationMatch(budgetTransactionsRouteProps)
export const selectBudgetHistoryMatch = generateSelectBackgroundLocationMatch(
  budgetHistoryRouteProps,
)
export const selectBudgetsAllBudgetsMatch =
  generateSelectBackgroundLocationMatch(budgetsAllBudgetsRouteProps)

export const selectBudgetsRequestsMatch = generateSelectBackgroundLocationMatch(
  budgetsRequestsRouteProps,
)
export const selectBudgetsNeedsApprovalMatch =
  generateSelectBackgroundLocationMatch(budgetsNeedsApprovalRouteProps)
export const selectProfileMatch =
  generateSelectBackgroundLocationMatch(profileRouteProps)
export const selectProfileOverviewMatch = generateSelectBackgroundLocationMatch(
  profileOverviewRouteProps,
)
export const selectProfileInfoMatch = generateSelectBackgroundLocationMatch(
  profileInfoRouteProps,
)
export const selectProfileNotificationsMatch =
  generateSelectBackgroundLocationMatch(profileNotificationsRouteProps)
export const selectProfileDeleteMatch = generateSelectBackgroundLocationMatch(
  profileDeleteRouteProps,
)
export const selectProfileSecurityMatch = generateSelectBackgroundLocationMatch(
  profileSecurityRouteProps,
)
export const selectTransactionsMatch = generateSelectBackgroundLocationMatch(
  transactionsRouteProps,
)
export const selectPeopleMatch =
  generateSelectBackgroundLocationMatch(peopleRouteProps)
export const selectPeopleMembersDetailMatch =
  generateSelectBackgroundLocationMatch(peopleMembersDetailRouteProps)
export const selectPeopleInvitedToOrgDetailMatch =
  generateSelectBackgroundLocationMatch(peopleInvitedToOrgDetailRouteProps)
export const selectAddPeopleMatch = generateSelectBackgroundLocationMatch(
  invitePeopleRouteProps,
)
export const selectPeopleMembersMatch = generateSelectBackgroundLocationMatch(
  peopleMembersRouteProps,
)
export const selectPeopleInvitedToOrgMatch =
  generateSelectBackgroundLocationMatch(peopleInvitedToOrgRouteProps)
export const selectExpensesMatch =
  generateSelectBackgroundLocationMatch(expensesRouteProps)
export const selectExpenseReportMatch = generateSelectBackgroundLocationMatch(
  expenseReportRouteProps,
)
export const selectBillPayMatch =
  generateSelectBackgroundLocationMatch(billPayRouteProps)
export const selectBillPayAllMatch =
  generateSelectBackgroundLocationMatch(billPayAllRouteProps)
export const selectBillPayNeedsApprovalMatch =
  generateSelectBackgroundLocationMatch(billPayNeedsApprovalRouteProps)
export const selectBusinessRulesMatch = generateSelectBackgroundLocationMatch(
  businessRulesRouteProps,
)
export const selectDepartmentsMatch = generateSelectBackgroundLocationMatch(
  departmentsRouteProps,
)
export const selectDepartmentDetailsMatch =
  generateSelectBackgroundLocationMatch(departmentDetailsRouteProps)
export const selectOrganizationInfoMatch =
  generateSelectBackgroundLocationMatch(organizationInfoRouteProps)
export const selectExpenseCategoriesMatch =
  generateSelectBackgroundLocationMatch(expenseCategoriesRouteProps)
export const selectAutomationRulesMatch = generateSelectBackgroundLocationMatch(
  automationRulesRouteProps,
)
export const selectApiProductMatch =
  generateSelectBackgroundLocationMatch(apiKeysRouteProps)
export const selectApiKeysProfileMatch = generateSelectBackgroundLocationMatch(
  apiKeysProfileRouteProps,
)
export const selectExpenseCategoryLabelsMatch =
  generateSelectBackgroundLocationMatch(expenseCategoryLabelsRouteProps)
export const selectOrganizationIntegrationsMatch =
  generateSelectBackgroundLocationMatch(orgIntegrationsRouteProps)
export const selectExpenseSettingsMatch = generateSelectBackgroundLocationMatch(
  expenseSettingsRouteProps,
)
export const selectReceiptRulesMatch = generateSelectBackgroundLocationMatch(
  receiptRulesRouteProps,
)
export const selectExpensesAllReportsMatch =
  generateSelectBackgroundLocationMatch(expensesAllReportsRouteProps)
export const selectExpensesOutOfPocketReportsMatch =
  generateSelectBackgroundLocationMatch(expensesOutOfPocketReportsRouteProps)
export const selectSubmitExpensesRouteMatch =
  generateSelectBackgroundLocationMatch(
    expensesOutOfPocketReportsYoursRouteProps,
  )
export const selectMyWalletReimbursementRouteMatch =
  generateSelectBackgroundLocationMatch(
    myWalletReimbursementRouteProps
  )
export const selectManagerReviewReimbursementsRouteMatch =
  generateSelectBackgroundLocationMatch(managerReviewReimbursementsRouteProps)
export const selectManagerReviewReimbursementRouteMatch =
  generateSelectBackgroundLocationMatch(managerReviewReimbursementRouteProps)
export const selectManagerReviewTransactionsRouteMatch =
  generateSelectBackgroundLocationMatch(managerReviewTransactionsRouteProps)
export const selectReimbursementsRouteMatch =
  generateSelectBackgroundLocationMatch(expensesOutOfPocketReportsAllRouteProps)
export const selectExpensesYourReportsMatch =
  generateSelectBackgroundLocationMatch(expensesYourReportsRouteProps)
export const selectExpensesTransactionsMatch =
  generateSelectBackgroundLocationMatch(expensesTransactionsRouteProps)
export const selectExpensesNeedsApprovalMatch =
  generateSelectBackgroundLocationMatch(expensesNeedsApprovalRouteProps)
export const selectExpensesExpenseReportsMatch =
  generateSelectBackgroundLocationMatch(expensesExpenseReportsRouteProps)
export const selectOrgExpenseReportsHistoricalReportsRouteProps =
  generateSelectBackgroundLocationMatch(
    orgExpenseReportsHistoricalReportsRouteProps,
  )
export const selectOrgExpenseReportRouteProps =
  generateSelectBackgroundLocationMatch(orgExpenseReportRouteProps)
export const selectVirtualCardsMatch = generateSelectBackgroundLocationMatch(
  virtualCardsRouteProps,
)
export const selectVirtualCardsAllCardsMatch =
  generateSelectBackgroundLocationMatch(virtualCardsAllCardsRouteProps)
export const selectVirtualCardsYourCardsMatch =
  generateSelectBackgroundLocationMatch(virtualCardsYourCardsRouteProps)
export const selectVirtualCardsRequestsMatch =
  generateSelectBackgroundLocationMatch(virtualCardsRequestsRouteProps)
export const selectVirtualCardsNeedsApprovalMatch =
  generateSelectBackgroundLocationMatch(virtualCardsNeedsApprovalRouteProps)
export const selectVirtualCardDetailRouteProps =
  generateSelectBackgroundLocationMatch(virtualCardDetailRouteProps)

export const selectMyWalletMatch =
  generateSelectBackgroundLocationMatch(myWalletRouteProps)
export const selectMyWalletVirtualCardsMatch =
  generateSelectBackgroundLocationMatch(myWalletVirtualCardsRouteProps)
export const selectMyWalletVirtualCardDetailMatch =
  generateSelectBackgroundLocationMatch(myWalletVirtualCardDetailRouteProps)
export const selectMyWalletPhysicalCardsMatch =
  generateSelectBackgroundLocationMatch(myWalletPhysicalCardsRouteProps)
export const selectMyWalletBudgetsMatch =
  generateSelectBackgroundLocationMatch(myWalletBudgetsRouteProps)
export const selectMyWalletBudgetsPendingMatch =
  generateSelectBackgroundLocationMatch(myWalletBudgetsPendingRouteProps)

export const selectCardsMatch =
  generateSelectBackgroundLocationMatch(cardsRouteProps)
export const selectCardsPhysicalCardsMatch =
  generateSelectBackgroundLocationMatch(cardsPhysicalCardsRouteProps)

export const selectVirtualCardDetailMatch =
  generateSelectBackgroundLocationMatch(virtualCardDetailRouteProps)

export const selectMyWalletTransactionsMatch =
  generateSelectBackgroundLocationMatch(myWalletTransactionsRouteProps)

// location matches
const generateSelectLocationMatch = <T>(
  routeProps: RouteProps,
): OutputSelector<
  SelectorArray,
  match<{ [K in keyof T]?: string }> | null,
  UnknownMemoizer
> =>
  createSelector([selectLocationFromState], (location) =>
    matchPath(location.pathname, routeProps),
  )
export const selectHelpMatch = generateSelectLocationMatch(helpRouteProps)
export const selectSearchMatch = generateSelectLocationMatch(searchRouteProps)
